/**
 * Adapter to transform post-level or analytics data into OptimalPostingTimesData.
 * If no endpoint exists, this provides a mocked adapter.
 *
 * Expected final shape:
 * OptimalPostingTimesData = { matrix, samples, timezone, totalPostsUsed, totalEngagements }
 */

/**
 * Create empty 7x24 matrix.
 * @returns {number[][]}
 */
function createEmptyMatrix() {
  return Array.from({ length: 7 }, () => Array(24).fill(0))
}

/**
 * Transform platform series (date-level) into a 7x24 matrix.
 * Uses engagements/impressions to derive engagement rate per day, then distributes
 * across hours using a seeded pattern for mock consistency.
 *
 * @param {Array<{ date: string, engagements?: number, impressions?: number, linkClicks?: number }>} series
 * @param {{ timezone?: string, metric?: 'engagement' | 'clicks' }} opts
 * @returns {{ matrix: number[7][24], samples: number[7][24], timezone: string, totalPostsUsed: number, totalEngagements: number }}
 */
export function adaptPlatformSeriesToOptimalPostingTimes(series, { timezone = 'UTC', metric = 'engagement' } = {}) {
  const matrix = createEmptyMatrix()
  const samples = createEmptyMatrix()

  if (!series || !Array.isArray(series) || series.length === 0) {
    return { matrix, samples, timezone, totalPostsUsed: 0, totalEngagements: 0 }
  }

  const seedFrom = (input) =>
    Array.from(String(input)).reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const seeded = (seed) => {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }
  const range = (seed, min, max) => min + seeded(seed) * (max - min)

  let totalPostsUsed = 0
  let totalEngagements = 0
  for (const entry of series) {
    totalEngagements += Math.max(0, Number(entry?.engagements) || 0)
    const dateStr = entry?.date
    if (!dateStr) continue
    const d = new Date(dateStr + 'T12:00:00Z')
    if (Number.isNaN(d.getTime())) continue
    const dayOfWeek = (d.getUTCDay() + 6) % 7 // Mon=0, Sun=6
    const impressions = entry.impressions ?? 0
    const engagements = entry.engagements ?? 0
    const linkClicks = entry.linkClicks ?? 0
    const postsPublished = Math.max(1, Math.round(entry.postsPublished ?? 2))
    const engagementRate = impressions > 0 ? (engagements / impressions) * 100 : 0
    const clicksRate = impressions > 0 ? (linkClicks / impressions) * 100 : 0
    const rate = metric === 'clicks' ? clicksRate : engagementRate

    // Distribute posts across hours for this day (seeded by date)
    const daySeed = seedFrom(dateStr)
    for (let i = 0; i < postsPublished; i++) {
      const hourSeed = daySeed + i * 17
      const hour = Math.floor(range(hourSeed, 0, 24))
      const h = Math.max(0, Math.min(23, hour))
      const slotRate = rate * (0.8 + seeded(hourSeed + 31) * 0.4)
      matrix[dayOfWeek][h] = (matrix[dayOfWeek][h] ?? 0) + slotRate
      samples[dayOfWeek][h] = (samples[dayOfWeek][h] ?? 0) + 1
      totalPostsUsed++
    }
  }

  // Average matrix values by sample count
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const n = samples[d][h] ?? 0
      if (n > 0) {
        matrix[d][h] = (matrix[d][h] ?? 0) / n
      }
    }
  }

  // Normalize to 0-100 scale for display
  let maxVal = 0
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const v = matrix[d][h] ?? 0
      if (v > maxVal) maxVal = v
    }
  }
  if (maxVal > 0) {
    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 24; h++) {
        matrix[d][h] = ((matrix[d][h] ?? 0) / maxVal) * 100
      }
    }
  }

  return { matrix, samples, timezone, totalPostsUsed, totalEngagements }
}

/**
 * List every calendar YYYY-MM-DD from start through end (inclusive), local date arithmetic.
 * @param {string} startKey
 * @param {string} endKey
 * @returns {string[]}
 */
export function enumerateCalendarDatesInRange(startKey, endKey) {
  if (!startKey || !endKey || startKey > endKey) return []
  const [sy, sm, sd] = startKey.split('-').map(Number)
  const [ey, em, ed] = endKey.split('-').map(Number)
  if (!sy || !sm || !sd || !ey || !em || !ed) return []
  const dates = []
  const cur = new Date(sy, sm - 1, sd)
  const end = new Date(ey, em - 1, ed)
  while (cur <= end) {
    const y = cur.getFullYear()
    const m = String(cur.getMonth() + 1).padStart(2, '0')
    const d = String(cur.getDate()).padStart(2, '0')
    dates.push(`${y}-${m}-${d}`)
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

function hourLabel12Compact(h) {
  const x = h % 12
  const suffix = h < 12 ? 'AM' : 'PM'
  const num = x === 0 ? 12 : x
  return `${num}${suffix}`
}

const HOUR_LABELS_CAL = Array.from({ length: 24 }, (_, h) => hourLabel12Compact(h))

function trimHourlyMatrixByActivity(matrix, samples, hourLabels) {
  if (!matrix.length || !hourLabels.length) return { matrix: [], samples: [], hours: [] }
  let first = 0
  let last = matrix.length - 1
  const rowHasActivity = (r) => {
    const vals = matrix[r] ?? []
    const srow = samples[r] ?? []
    return vals.some((v, c) => Number(v) > 0 || Number(srow[c] ?? 0) > 0)
  }
  while (first <= last && !rowHasActivity(first)) first += 1
  while (last >= first && !rowHasActivity(last)) last -= 1
  if (first > last) {
    return { matrix, samples, hours: hourLabels }
  }
  return {
    matrix: matrix.slice(first, last + 1),
    samples: samples.slice(first, last + 1),
    hours: hourLabels.slice(first, last + 1),
  }
}

/**
 * Optimal-posting-style hourly × calendar day matrix for a selected date range (aggregated series).
 * Rows = hours (trimmed to active span), columns = consecutive calendar days in range.
 * Matrix values: 0–100 engagement index; samples: modeled post counts per cell.
 *
 * @param {Array<{ date: string, impressions?: number, engagements?: number, linkClicks?: number, postsPublished?: number }>} series
 * @param {string} startKey - YYYY-MM-DD
 * @param {string} endKey - YYYY-MM-DD
 * @param {{ metric?: 'engagement' | 'clicks' }} [opts]
 * @returns {{ dates: string[], matrix: number[][], samples: number[][], hours: string[], totalPostsUsed: number, totalEngagements: number }}
 */
export function adaptPlatformSeriesToOptimalPostingCalendarHourly(series, startKey, endKey, { metric = 'engagement' } = {}) {
  const dates = enumerateCalendarDatesInRange(startKey, endKey)
  const n = dates.length
  const empty = { dates, matrix: [], samples: [], hours: [], totalPostsUsed: 0, totalEngagements: 0 }
  if (n === 0) return empty

  const sum = Array.from({ length: 24 }, () => Array(n).fill(0))
  const samples = Array.from({ length: 24 }, () => Array(n).fill(0))

  const seedFrom = (input) =>
    Array.from(String(input)).reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const seeded = (seed) => {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }
  const range = (seed, min, max) => min + seeded(seed) * (max - min)

  const byDate = new Map()
  if (series && Array.isArray(series)) {
    for (const entry of series) {
      if (entry?.date) byDate.set(entry.date, entry)
    }
  }

  let totalPostsUsed = 0
  let totalEngagements = 0

  dates.forEach((dateStr, col) => {
    const entry = byDate.get(dateStr)
    if (!entry) return
    totalEngagements += Math.max(0, Number(entry.engagements) || 0)
    const impressions = entry.impressions ?? 0
    const engagements = entry.engagements ?? 0
    const linkClicks = entry.linkClicks ?? 0
    const postsPublished = Math.max(1, Math.round(entry.postsPublished ?? 2))
    const engagementRate = impressions > 0 ? (engagements / impressions) * 100 : 0
    const clicksRate = impressions > 0 ? (linkClicks / impressions) * 100 : 0
    const rate = metric === 'clicks' ? clicksRate : engagementRate

    const daySeed = seedFrom(dateStr)
    for (let i = 0; i < postsPublished; i += 1) {
      const hourSeed = daySeed + i * 17
      const hour = Math.floor(range(hourSeed, 0, 24))
      const h = Math.max(0, Math.min(23, hour))
      const slotRate = rate * (0.8 + seeded(hourSeed + 31) * 0.4)
      sum[h][col] = (sum[h][col] ?? 0) + slotRate
      samples[h][col] = (samples[h][col] ?? 0) + 1
      totalPostsUsed += 1
    }
  })

  const matrix = sum.map((row, h) =>
    row.map((_, col) => {
      const c = samples[h][col] ?? 0
      if (c <= 0) return 0
      return (sum[h][col] ?? 0) / c
    }),
  )

  let maxVal = 0
  for (let h = 0; h < 24; h += 1) {
    for (let c = 0; c < n; c += 1) {
      const v = matrix[h][c] ?? 0
      if (v > maxVal) maxVal = v
    }
  }
  if (maxVal > 0) {
    for (let h = 0; h < 24; h += 1) {
      for (let c = 0; c < n; c += 1) {
        matrix[h][c] = ((matrix[h][c] ?? 0) / maxVal) * 100
      }
    }
  }

  const trimmed = trimHourlyMatrixByActivity(matrix, samples, HOUR_LABELS_CAL)
  return {
    dates,
    matrix: trimmed.matrix,
    samples: trimmed.samples,
    hours: trimmed.hours,
    totalPostsUsed,
    totalEngagements,
  }
}

/**
 * List YYYY-MM for every calendar month that intersects [startKey, endKey] (inclusive).
 * @param {string} startKey
 * @param {string} endKey
 * @returns {string[]}
 */
export function enumerateYearMonthsInRange(startKey, endKey) {
  if (!startKey || !endKey || startKey > endKey) return []
  const [sy, sm] = startKey.split('-').map(Number)
  const [ey, em] = endKey.split('-').map(Number)
  if (!sy || !sm || !ey || !em) return []
  const months = []
  let y = sy
  let m = sm
  let done = false
  while (!done) {
    months.push(`${y}-${String(m).padStart(2, '0')}`)
    if (y === ey && m === em) {
      done = true
    } else {
      m += 1
      if (m > 12) {
        m = 1
        y += 1
      }
    }
  }
  return months
}

/**
 * Hourly × calendar month matrix for the selected range (same modeling as calendar-daily).
 * Columns = YYYY-MM buckets; rows = hours (trimmed to active span).
 *
 * @param {Array<{ date: string, impressions?: number, engagements?: number, linkClicks?: number, postsPublished?: number }>} series
 * @param {string} startKey
 * @param {string} endKey
 * @param {{ metric?: 'engagement' | 'clicks' }} [opts]
 * @returns {{ dates: string[], matrix: number[][], samples: number[][], hours: string[], totalPostsUsed: number, totalEngagements: number }}
 */
export function adaptPlatformSeriesToOptimalPostingMonthlyHourly(series, startKey, endKey, { metric = 'engagement' } = {}) {
  const monthKeys = enumerateYearMonthsInRange(startKey, endKey)
  const n = monthKeys.length
  const empty = { dates: monthKeys, matrix: [], samples: [], hours: [], totalPostsUsed: 0, totalEngagements: 0 }
  if (n === 0) return empty

  const sum = Array.from({ length: 24 }, () => Array(n).fill(0))
  const samples = Array.from({ length: 24 }, () => Array(n).fill(0))

  const seedFrom = (input) =>
    Array.from(String(input)).reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const seeded = (seed) => {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }
  const range = (seed, min, max) => min + seeded(seed) * (max - min)

  const monthIndex = new Map(monthKeys.map((k, i) => [k, i]))

  let totalPostsUsed = 0
  let totalEngagements = 0

  if (series && Array.isArray(series)) {
    for (const entry of series) {
      const dateStr = entry?.date
      if (!dateStr || dateStr < startKey || dateStr > endKey) continue
      const ym = dateStr.slice(0, 7)
      const col = monthIndex.get(ym)
      if (col === undefined) continue

      totalEngagements += Math.max(0, Number(entry?.engagements) || 0)
      const impressions = entry.impressions ?? 0
      const engagements = entry.engagements ?? 0
      const linkClicks = entry.linkClicks ?? 0
      const postsPublished = Math.max(1, Math.round(entry.postsPublished ?? 2))
      const engagementRate = impressions > 0 ? (engagements / impressions) * 100 : 0
      const clicksRate = impressions > 0 ? (linkClicks / impressions) * 100 : 0
      const rate = metric === 'clicks' ? clicksRate : engagementRate

      const daySeed = seedFrom(dateStr)
      for (let i = 0; i < postsPublished; i += 1) {
        const hourSeed = daySeed + i * 17
        const hour = Math.floor(range(hourSeed, 0, 24))
        const h = Math.max(0, Math.min(23, hour))
        const slotRate = rate * (0.8 + seeded(hourSeed + 31) * 0.4)
        sum[h][col] = (sum[h][col] ?? 0) + slotRate
        samples[h][col] = (samples[h][col] ?? 0) + 1
        totalPostsUsed += 1
      }
    }
  }

  const matrix = sum.map((row, h) =>
    row.map((_, col) => {
      const c = samples[h][col] ?? 0
      if (c <= 0) return 0
      return (sum[h][col] ?? 0) / c
    }),
  )

  let maxVal = 0
  for (let h = 0; h < 24; h += 1) {
    for (let c = 0; c < n; c += 1) {
      const v = matrix[h][c] ?? 0
      if (v > maxVal) maxVal = v
    }
  }
  if (maxVal > 0) {
    for (let h = 0; h < 24; h += 1) {
      for (let c = 0; c < n; c += 1) {
        matrix[h][c] = ((matrix[h][c] ?? 0) / maxVal) * 100
      }
    }
  }

  const trimmed = trimHourlyMatrixByActivity(matrix, samples, HOUR_LABELS_CAL)
  return {
    dates: monthKeys,
    matrix: trimmed.matrix,
    samples: trimmed.samples,
    hours: trimmed.hours,
    totalPostsUsed,
    totalEngagements,
  }
}

/**
 * Parse compact 12h label from {@link HOUR_LABELS_CAL} (e.g. "12AM", "3PM") to hour 0–23.
 * @param {string} label
 * @returns {number | null}
 */
export function parseHourLabel12ToIndex(label) {
  if (label == null) return null
  const s = String(label)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
  const m = s.match(/^(\d{1,2})(AM|PM)$/)
  if (!m) return null
  let n = parseInt(m[1], 10)
  if (n < 1 || n > 12) return null
  const pm = m[2] === 'PM'
  if (n === 12) return pm ? 12 : 0
  return pm ? n + 12 : n
}

/**
 * Map trimmed hour rows (labels aligned with calendar/monthly adapters) into fixed 24×N matrices
 * so the chart Y-axis matches the weekly 24h scale.
 *
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @param {string[]} hourLabels
 * @param {number} colCount
 * @returns {{ matrix: number[][], samples: number[][] }}
 */
export function expandTrimmedHourlyMatrixTo24(matrix, samples, hourLabels, colCount, engagements = null) {
  const outM = Array.from({ length: 24 }, () => Array(colCount).fill(0))
  const outS = Array.from({ length: 24 }, () => Array(colCount).fill(0))
  const outE = Array.from({ length: 24 }, () => Array(colCount).fill(null))
  if (!Array.isArray(matrix) || !Array.isArray(samples) || !Array.isArray(hourLabels) || colCount <= 0) {
    return { matrix: outM, samples: outS, engagements: outE }
  }
  for (let r = 0; r < hourLabels.length; r += 1) {
    const h = parseHourLabel12ToIndex(hourLabels[r])
    if (h == null || h < 0 || h > 23) continue
    for (let c = 0; c < colCount; c += 1) {
      outM[h][c] = Number(matrix[r]?.[c] ?? 0)
      outS[h][c] = Number(samples[r]?.[c] ?? 0)
      const engagement = engagements?.[r]?.[c]
      outE[h][c] = engagement == null ? null : Number(engagement)
    }
  }
  return { matrix: outM, samples: outS, engagements: outE }
}
